import type { FilterRuleEntity } from '@/shared/components/query-builder'
import type { ErrorSchema } from '@/shared/tools/json-schema-utils'
import type { StrategyModalFormData } from '../model/types'

import { Fragment, useEffect, useRef } from 'react'
import { set } from 'lodash/fp'
import { CircleHelpIcon, GripVerticalIcon, PlusIcon, XIcon } from 'lucide-react'
import {
	Controller,
	useFieldArray,
	useFormContext,
	useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { getLocalization } from '@/shared/components/filter/localization'
import {
	QueryBuilder,
	QueryBuilderAddButton,
	QueryBuilderEmpty,
	QueryBuilderRulesList,
} from '@/shared/components/query-builder'
import Combobox from '@/shared/components/query-builder/components/Combobox'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Sortable,
	SortableContent,
	SortableItem,
	SortableItemHandle,
	SortableOverlay,
} from '@/shared/components/ui/sortable'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { useAppStore } from '@/shared/contexts/app'
import { ampli } from '@/shared/lib/ampli'
import { uuid } from '@/shared/tools/utils'

import { useStrategyFieldsConfig } from '../lib/useStrategyFieldsConfig'
import { AnchorFamilyCardHeader } from './AnchorFamilyCardHeader'

export const AnchorFamiliesTab = () => {
	const { t } = useTranslation()

	useEffect(() => {
		ampli.anchoringAnchorStrategyOpenFamilies()
	}, [])
	const name = 'settings.groups'
	const { formState } = useFormContext()
	const lastAddedFamilyRef = useRef<HTMLDivElement>(null)
	const { fields, append, remove } = useFieldArray<
		StrategyModalFormData,
		'settings.groups',
		'_id'
	>({
		name,
		keyName: '_id',
	})
	const error = formState.errors?.settings as ErrorSchema

	const handleScroll = () => {
		setTimeout(() => {
			lastAddedFamilyRef?.current?.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}, 100)
	}

	return (
		<div className='space-y-2'>
			{fields?.map((field, index: number) => {
				const isLastItem = index === fields.length - 1

				return (
					<Fragment key={field._id}>
						<Card
							className='w-full shadow-none'
							ref={isLastItem ? lastAddedFamilyRef : null}
						>
							<AnchorFamilyCardHeader
								id={field.id!}
								name={`${name}.${index}.name`}
								onClick={() => remove(index)}
							/>
							<CardContent>
								<FamiliesPanel index={index} />
							</CardContent>
						</Card>
					</Fragment>
				)
			})}
			<Button
				variant='ghost'
				data-testid='add-family-button'
				onClick={() => {
					ampli.anchoringAnchorStrategyAddFamily()
					append({
						name: `Family ${fields.length + 1}`,
						id: uuid(),
						filters: [],
						aggregation: [],
						clusterization: [],
					})
					handleScroll()
				}}
			>
				<PlusIcon />
				{t(
					'anchoring_management.strategy.tab.anchor_families.add_family.title'
				)}
			</Button>
			{error?.groups?.root?.message && (
				<p className='text-sm text-destructive'>
					{error?.groups?.root?.message}
				</p>
			)}
			{error?.groups?.message && (
				<p className='text-sm text-destructive'>{error?.groups?.message}</p>
			)}
		</div>
	)
}

const DEFAULT_CLUSTER_PERCENT = 5

function FamiliesPanel({ index }: { index: number }) {
	const { t } = useTranslation()
	const dateFormat = useAppStore((state) => state.dateFormat)
	const { control } = useFormContext<StrategyModalFormData>()

	const {
		isLoading,
		fieldConfig,
		aggregationColumnOptions: aggregationColumnsOptions,
		clusterizationColumnOptions,
	} = useStrategyFieldsConfig()

	const {
		fields: clusterFields,
		append: appendCluster,
		remove: removeCluster,
	} = useFieldArray({
		control,
		name: `settings.groups.${index}.clusterization`,
	})

	// `clusterFields` is a snapshot taken on add/remove, so the live values drive the
	// per-row option filtering that keeps a column from being picked twice.
	const clusterization =
		useWatch({ control, name: `settings.groups.${index}.clusterization` }) || []
	const usedClusterizationColumns = clusterization.map(({ column }) => column)

	const handleAddCluster = () => {
		const nextColumn = clusterizationColumnOptions.find(
			(option) => !usedClusterizationColumns.includes(option.value as string)
		)?.value as string

		appendCluster({
			column: nextColumn || '',
			percent: DEFAULT_CLUSTER_PERCENT,
		})
	}

	return isLoading ? (
		<div className='flex flex-col space-y-2'>
			<Skeleton className='h-10 w-full' />
			<Skeleton className='h-10 w-full' />
		</div>
	) : (
		<ErrorBoundary
			fallback={() => (
				<Alert variant='destructive'>
					<AlertDescription>
						🐞{' '}
						{t('ui_fragment_error', {
							defaultValue:
								'Oops ... it looks like an unexpected error has occurred. Our team`s been notified.',
						})}
					</AlertDescription>
				</Alert>
			)}
		>
			<div className='space-y-4'>
				{/* Filters */}
				<Controller
					name={`settings.groups.${index}.filters`}
					control={control}
					render={({ field: { value, onChange }, fieldState: { error } }) => (
						<div className='grid gap-1.5'>
							<Label className='text-sm'>{t('app.filters')}</Label>
							<QueryBuilder
								dateFormat={dateFormat}
								rules={value || []}
								fieldConfig={fieldConfig}
								locale={getLocalization(t)}
								onRulesChange={(rules: FilterRuleEntity[]) => {
									ampli.anchoringAnchorStrategyAddFamilyFilters({
										filters_value: rules.map((f) => f.name),
									})
									onChange(rules.map(formatFilterRule))
								}}
							>
								<QueryBuilderEmpty className='border-b' />
								<QueryBuilderRulesList className='max-h-100 overflow-y-auto border-y py-2' />
								{error && (
									<p className='text-sm text-destructive'>
										{error.message || t('validation.invalid_rules')}
									</p>
								)}
								<QueryBuilderAddButton
									data-testid='filters-popover-add'
									className='mr-auto'
								>
									<PlusIcon />
									{t('filters_add_filter')}
								</QueryBuilderAddButton>
							</QueryBuilder>
						</div>
					)}
				/>

				{/* Aggregation */}
				<Controller
					name={`settings.groups.${index}.aggregation`}
					control={control}
					render={({
						field: { value: aggregationRules, onChange: setAggregation },
					}) => (
						<div className='space-y-2'>
							<Label className='mb-2 text-sm'>
								{t(
									'anchoring_management.strategy.tab.anchor_families.aggregation_column.title'
								)}
							</Label>
							<Sortable
								value={aggregationRules}
								getItemValue={(item: string) => item}
								onValueChange={setAggregation}
							>
								<SortableContent>
									{aggregationRules?.map((rule: string, ruleIndex: number) => {
										const key = `${ruleIndex}_${rule}`

										return (
											<SortableItem value={rule} key={key}>
												<div className='flex space-y-2 space-x-2' role='list'>
													<Combobox
														name={`aggregation-${ruleIndex}`}
														value={rule}
														onChange={(value) => {
															ampli.anchoringAnchorStrategyUpdateAggregationRule(
																{ filter_value: value }
															)
															setAggregation(
																set(ruleIndex, value, aggregationRules)
															)
														}}
														options={aggregationColumnsOptions
															.filter(
																(o) =>
																	!aggregationRules.includes(o.value) ||
																	o.value === rule
															)
															.map((o) => ({ value: o.value, label: o.label }))}
														placeholder={t('general_select')}
														locale={getLocalization(t)}
														className='w-1/2'
													/>
													<div className='flex space-x-2'>
														<Button
															size='icon'
															variant='ghost'
															onClick={() =>
																setAggregation(
																	aggregationRules.filter(
																		(_: string, i: number) => i !== ruleIndex
																	)
																)
															}
														>
															<XIcon />
														</Button>
														<SortableItemHandle asChild>
															<Button
																variant='ghost'
																size='icon'
																disabled={aggregationRules.length === 1}
															>
																<GripVerticalIcon className='text-muted-foreground' />
															</Button>
														</SortableItemHandle>
													</div>
												</div>
											</SortableItem>
										)
									})}
								</SortableContent>
								<SortableOverlay>
									<div className='flex w-full items-center gap-3'>
										<div className='h-8 w-6/7 justify-between rounded-lg bg-accent' />
										<div className='flex gap-2'>
											<div className='h-8 w-12 rounded-lg bg-accent' />
											<div className='h-8 w-12 rounded-lg bg-accent' />
										</div>
									</div>
								</SortableOverlay>
							</Sortable>
							<Button
								disabled={
									aggregationRules.length === aggregationColumnsOptions.length
								}
								variant='ghost'
								className='mr-auto'
								data-testid='anchor-strategy-modal-family-tab-addrule-button'
								onClick={() => {
									ampli.anchoringAnchorStrategyAddAggregation()
									const defaultRuleValue =
										aggregationColumnsOptions.find(
											(i) => !aggregationRules.includes(i.value as string)
										)?.value || 'sku'
									setAggregation([...aggregationRules, defaultRuleValue])
								}}
							>
								<PlusIcon />
								{t(
									'anchoring_management.strategy.tab.anchor_families.add_rule'
								)}
							</Button>
						</div>
					)}
				/>

				{/* Clusterization */}
				<div className='space-y-2'>
					<Label className='text-sm'>
						{t(
							'anchoring_management.strategy.tab.anchor_families.clusterization.title',
							{ defaultValue: 'Clusterization' }
						)}
					</Label>
					{clusterization.length === 0 ? (
						<p
							className='text-sm text-muted-foreground'
							data-testid={`anchor-family-${index}-cluster-empty`}
						>
							{t(
								'anchoring_management.strategy.tab.anchor_families.clusterization.empty',
								{
									defaultValue:
										'No clustering rules yet. Products are split only by the aggregation columns.',
								}
							)}
						</p>
					) : (
						<div className='space-y-2'>
							<div className='flex items-center gap-4'>
								<Label className='flex-1 text-xs text-muted-foreground'>
									{t(
										'anchoring_management.strategy.tab.anchor_families.clusterization.column_label',
										{ defaultValue: 'Column' }
									)}
								</Label>
								<Label className='flex w-1/4 items-center gap-1 text-xs text-muted-foreground'>
									{t(
										'anchoring_management.strategy.tab.anchor_families.clusterization.percent_label',
										{ defaultValue: 'Tolerance %' }
									)}
									<Tooltip>
										<TooltipTrigger asChild>
											<CircleHelpIcon className='size-3 text-muted-foreground' />
										</TooltipTrigger>
										<TooltipContent side='top'>
											{t(
												'anchoring_management.strategy.tab.anchor_families.clusterization.percent_tooltip',
												{
													defaultValue:
														'Allowed deviation (0–100%) from the cluster center. Products whose column value falls within this range are grouped into the same cluster.',
												}
											)}
										</TooltipContent>
									</Tooltip>
								</Label>
								<div className='w-9 shrink-0' />
							</div>
							{clusterFields.map((clusterField, conditionIndex) => (
								<div
									key={clusterField.id}
									className='flex items-start gap-4'
									data-testid={`anchor-family-${index}-cluster-row-${conditionIndex}`}
								>
									<Controller
										name={`settings.groups.${index}.clusterization.${conditionIndex}.column`}
										control={control}
										render={({
											field: { value, onChange },
											fieldState: { error },
										}) => (
											<div
												className='flex-1 space-y-1'
												data-testid={`anchor-family-${index}-cluster-column-${conditionIndex}`}
											>
												<Combobox
													name={`clusterization-${index}-${conditionIndex}`}
													value={value}
													onChange={onChange}
													options={clusterizationColumnOptions
														.filter(
															(option) =>
																option.value === value ||
																!usedClusterizationColumns.includes(
																	option.value as string
																)
														)
														.map((option) => ({
															value: option.value as string,
															label: option.label,
														}))}
													placeholder={t('general_select')}
													locale={getLocalization(t)}
													invalid={!!error?.message}
													className='w-full'
												/>
												{error?.message && (
													<p className='text-sm text-destructive'>
														{error.message}
													</p>
												)}
											</div>
										)}
									/>
									<Controller
										name={`settings.groups.${index}.clusterization.${conditionIndex}.percent`}
										control={control}
										render={({
											field: { value, onChange },
											fieldState: { error },
										}) => (
											<div className='w-1/4 space-y-1'>
												<Input
													type='number'
													min={0}
													max={100}
													placeholder='0'
													className='w-full'
													aria-invalid={!!error?.message}
													value={value ?? ''}
													onChange={(e) =>
														onChange(
															e.target.value !== ''
																? Number(e.target.value)
																: null
														)
													}
													data-testid={`anchor-family-${index}-cluster-percent-${conditionIndex}`}
												/>
												{error?.message && (
													<p className='text-sm text-destructive'>
														{error.message}
													</p>
												)}
											</div>
										)}
									/>
									<Button
										size='icon'
										variant='ghost'
										className='shrink-0'
										onClick={() => removeCluster(conditionIndex)}
										data-testid={`anchor-family-${index}-cluster-remove-${conditionIndex}`}
									>
										<XIcon />
									</Button>
								</div>
							))}
						</div>
					)}
					<Button
						variant='ghost'
						className='mr-auto'
						disabled={
							clusterization.length >= clusterizationColumnOptions.length
						}
						onClick={handleAddCluster}
						data-testid={`anchor-family-${index}-add-cluster-button`}
					>
						<PlusIcon />
						{t(
							'anchoring_management.strategy.tab.anchor_families.clusterization.add_cluster',
							{ defaultValue: 'Add cluster' }
						)}
					</Button>
				</div>
			</div>
		</ErrorBoundary>
	)
}
